import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { ArrowBackOutline } from '@lyearn/icons';
import { Breadcrumb, LinkButton, LyearnBreadcrumbs } from '@lyearn/ui';
import { Link } from '@/components/Link';
import useNavigation from '@/hooks/useNavigation';

import { breadcrumbStyles } from './styles';

export interface BreadcrumbProps {
  to?: string;
  label: string;
  overrides?: React.ComponentProps<typeof Breadcrumb>['overrides'];
}

export interface BreadcrumbsProps {
  breadcrumbs: BreadcrumbProps[];
  className?: string;
  backLink?: string;
  backIcon?: JSX.Element;

  /* to prevent back or do something when executing */
  onBackClick?: React.MouseEventHandler;
}

export const BackButton = (props: { onClick?: () => void }) => {
  return (
    <LinkButton className="group flex-shrink-0 py-2 px-4" onClick={props.onClick}>
      <ArrowBackOutline className="fill-current text-icon-primary group-hover:text-icon-active rtl:rotate-180" />
    </LinkButton>
  );
};

const BREADCRUMB_LAST_ITEM_OVERRIDES = {
  Typography: {
    className: breadcrumbStyles,
  },
};

function Breadcrumbs(props: BreadcrumbsProps) {
  const { breadcrumbs, className = 'ps-12', backLink, backIcon, onBackClick } = props;

  const navigation = useNavigation();
  const { isMobile } = useScreen();

  const BackIcon = backLink ? (
    <Link to={backLink} onClick={onBackClick ?? undefined}>
      {backIcon || <BackButton />}
    </Link>
  ) : (
    <>{backIcon || <BackButton onClick={(onBackClick as any) ?? navigation.back} />}</>
  );

  return (
    <>
      {BackIcon}
      {isMobile ? null : (
        <LyearnBreadcrumbs className={className} maxItems={4}>
          {breadcrumbs.map((breadcrumb, index) => {
            const isLastItem = index === breadcrumbs.length - 1;
            const overrides =
              breadcrumb.overrides ||
              (isLastItem && !breadcrumb.to ? BREADCRUMB_LAST_ITEM_OVERRIDES : undefined);

            return (
              <Breadcrumb
                key={index}
                component={Link as any}
                overrides={overrides}
                size={isLastItem ? 'medium' : 'small'}
                to={breadcrumb.to}>
                {breadcrumb.label}
              </Breadcrumb>
            );
          })}
        </LyearnBreadcrumbs>
      )}
    </>
  );
}

export default Breadcrumbs;
